import * as DialogPrimitive from "@radix-ui/react-dialog";
import type {ReactNode} from "react";
import {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type {RouteObject, Location} from "react-router";
import {useLocation, useNavigate, useRoutes} from "react-router";
import {Drawer as Vaul} from "vaul";
import Spinner from "../shared/ui/Spinner";
import {transitions} from "../styles/decoration.css";
import {defaultTheme} from "../styles/themes/color-themes.css";
import {useEsc} from "../utils/use-keypress";
import {useMyTransition} from "../utils/use-my-transition";
import {Box, Col, css} from "./Box";

type SharedDrawerContentProps = {children: ReactNode; title: string};

const Modal = (props: SharedDrawerContentProps & {open: boolean; onClose: () => void}) => {
  const {children, onClose, open, title} = props;
  const overlayNodeRef = useRef<HTMLDivElement | null>(null);
  const contentNodeRef = useRef<HTMLDivElement | null>(null);
  useEsc(onClose);
  useLayoutEffect(() => {
    let raf = null as null | ReturnType<typeof requestAnimationFrame>;
    if (open) {
      raf = requestAnimationFrame(() => {
        raf = null;
        const overlayNode = overlayNodeRef.current!;
        const contentNode = contentNodeRef.current!;
        overlayNode.style.opacity = "1";
        contentNode.style.opacity = "1";
        contentNode.style.transform = "translate(-50%, -50%)";
      });
    } else {
      const overlayNode = overlayNodeRef.current!;
      const contentNode = contentNodeRef.current!;
      overlayNode.style.opacity = "0";
      contentNode.style.opacity = "0";
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [open]);
  return (
    <DialogPrimitive.Root
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      open={true}
    >
      <DialogPrimitive.Overlay
        ref={overlayNodeRef}
        className={css(
          {
            bg: "backdrop",
            bgAlpha: "80%",
            position: "fixed",
            inset: "0",
          },
          defaultTheme,
          transitions.transformAndColors
        )}
        style={{opacity: 0}}
      />
      <DialogPrimitive.Content
        ref={contentNodeRef}
        className={css(
          {
            bg: "surface",
            position: "fixed",
            pa: "16",
            rounded: "4",
          },
          transitions.transformAndColors
        )}
        style={{
          opacity: 0,
          transform: "translate(-50%, -200%) scale(0.95)",

          top: "50%",
          left: "50%",
          width: "90vw",
          maxWidth: 450,
          maxHeight: "85vh",
        }}
      >
        <DialogPrimitive.Title className="DialogTitle">{title}</DialogPrimitive.Title>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};

// vaul's open transition is 0.5s
const DRAWER_OPEN_DURATION_MS = 500;

/**
 * Focuses the drawer's `[data-drawer-autofocus]` element once the open animation is done.
 *
 * Focusing (e.g. via `autoFocus`) while the drawer still slides in opens the keyboard against the
 * drawer's off-screen position, and the browser pans the page so far that the input ends up out of
 * view. Rendered inside the drawer's Suspense boundary, so content that suspends gets focused once
 * it shows up.
 */
const FocusAfterOpen = ({openedAt}: {openedAt: number}) => {
  const markerRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const delay = Math.max(0, openedAt + DRAWER_OPEN_DURATION_MS - Date.now());
    const id = setTimeout(() => {
      const content = markerRef.current?.closest("[data-vaul-drawer]");
      content?.querySelector<HTMLElement>("[data-drawer-autofocus]")?.focus();
    }, delay);
    return () => clearTimeout(id);
  }, [openedAt]);
  return <span ref={markerRef} hidden />;
};

const DrawerBody = ({children}: {children: ReactNode}) => {
  // mounts when the drawer opens, as vaul unmounts the content while closed
  const [openedAt] = useState(() => Date.now());
  return (
    <Col overflow="auto" px="12" pt="12">
      <Suspense fallback={<Spinner size="48" />}>
        {children}
        <FocusAfterOpen openedAt={openedAt} />
      </Suspense>
    </Col>
  );
};

const Drawer = (
  props: SharedDrawerContentProps & {open: boolean; onClose: () => void; handleOnly?: boolean}
) => {
  const {children, onClose, open, title} = props;
  useEsc(onClose);
  return (
    <Vaul.Root shouldScaleBackground open={open} onClose={onClose}>
      <Vaul.Portal>
        <Vaul.Overlay
          className={css(
            {bg: "backdrop", bgAlpha: "80%", position: "fixed", inset: "0"},
            defaultTheme
          )}
        />
        <Vaul.Title>{title}</Vaul.Title>
        <Vaul.Description />
        <Vaul.Content
          className={css(
            {
              bg: "surface",
              position: "fixed",
              bottom: "0",
              left: "0",
              right: "0",
              maxHeight: "_drawerHeight",
              display: "flex",
              flexDir: "column",
              roundedTop: "4",
            },
            defaultTheme
          )}
        >
          <DrawerBody>{children}</DrawerBody>
          <Col absolute bottom="100%" left="0" right="0" pb="8" align="center">
            <Box
              surface
              height="_drawerHandleHeight"
              width="100%"
              rounded="4"
              maxWidth="_drawerHandleWidth"
            />
          </Col>
        </Vaul.Content>
      </Vaul.Portal>
    </Vaul.Root>
  );
};

type ControlledDrawerProps = SharedDrawerContentProps & {open: boolean; onClose?: () => void};
export const ControlledDrawer = (props: ControlledDrawerProps) => {
  const {open, onClose, ...rest} = props;
  const handleClose = () => {
    if (open && onClose) onClose();
  };

  return <Drawer open={open} {...rest} onClose={handleClose} />;
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const DrawerRouteCtx = createContext<boolean>(null as any);

export const RouteDrawer = (
  props: {backPath: string; asModal?: boolean} & SharedDrawerContentProps
) => {
  const {backPath, asModal, ...rest} = props;
  const navigate = useNavigate();
  const unmounting = useContext(DrawerRouteCtx);
  const handleClose = () => {
    if (!unmounting) navigate(backPath);
  };
  return asModal ? (
    <Modal onClose={handleClose} open={!unmounting} {...rest} />
  ) : (
    <Drawer onClose={handleClose} open={!unmounting} {...rest} />
  );
};

const RenderRoutes = (props: {routes: RouteObject[]; location: Location; unmounting: boolean}) => {
  const {routes, location, unmounting} = props;
  const routeEl = useRoutes(routes, location);
  return routeEl && <DrawerRouteCtx.Provider value={unmounting}>{routeEl}</DrawerRouteCtx.Provider>;
};

export const DrawerRoutes = ({routes}: {routes: RouteObject[]}) => {
  const location = useLocation();
  const pathTransitions = useMyTransition(location, location.pathname, {canRecover: false});
  return pathTransitions((loc, unmounting) => (
    <RenderRoutes routes={routes} location={loc} unmounting={unmounting} />
  ));
};
